import React, { useState } from 'react';
import { Mic, Play, MessageCircle, Send, Pencil, X, Camera } from 'lucide-react';
import MediaCapture from '../../MediaCapture';
import MemberNameLink from '../../common/MemberNameLink';
import { getUser } from '../../../utils/secureStorage';

// API base URL for media files
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

function VoiceNote({ duration, mediaUrl, mediaType = 'audio' }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [error, setError] = useState(null);
  const mediaRef = React.useRef(null);

  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Build full URL from relative path
  const getFullMediaUrl = (url) => {
    if (!url) return null;
    // If already a full URL, return as-is
    if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('blob:')) {
      return url;
    }
    // Otherwise prepend API base URL
    return `${API_BASE_URL}${url}`;
  };

  const handlePlayPause = () => {
    // For video, open modal instead of inline play
    if (mediaType === 'video') {
      setShowVideoModal(true);
      return;
    }

    if (!mediaRef.current) return;

    if (isPlaying) {
      mediaRef.current.pause();
    } else {
      mediaRef.current.play().catch(err => {
        console.error('Error playing media:', err);
        setError('Could not play');
      });
    }
    setIsPlaying(!isPlaying);
  };

  const handleEnded = () => {
    setIsPlaying(false);
  };

  const handleError = (e) => {
    console.error('Media error:', e);
    setError('Playback error');
  };

  const closeVideoModal = () => {
    setShowVideoModal(false);
  };

  const fullUrl = getFullMediaUrl(mediaUrl);

  return (
    <>
      <div className="voice-note" onClick={handlePlayPause}>
        <span className="voice-note-icon">{mediaType === 'video' ? '🎬' : '🎤'}</span>
        <span className="voice-note-duration">
          {error ? error : `${mediaType === 'video' ? 'Video' : 'Voice'} message · ${formatDuration(duration)}`}
        </span>
        <div className={`voice-note-play ${isPlaying ? 'playing' : ''}`}>
          {isPlaying ? <span className="pause-icon">❚❚</span> : <Play size={12} fill="#fff" />}
        </div>
        {fullUrl && mediaType === 'audio' && (
          <audio
            ref={mediaRef}
            src={fullUrl}
            onEnded={handleEnded}
            onError={handleError}
            style={{ display: 'none' }}
          />
        )}
      </div>

      {/* Video Modal */}
      {showVideoModal && fullUrl && (
        <div className="video-message-modal" onClick={closeVideoModal}>
          <div className="video-message-content" onClick={(e) => e.stopPropagation()}>
            <video
              controls
              autoPlay
              src={fullUrl}
              style={{ maxWidth: '100%', maxHeight: '70vh', borderRadius: '8px' }}
            >
              Your browser does not support the video tag.
            </video>
            <button className="video-message-close" onClick={closeVideoModal}>
              ×
            </button>
          </div>
        </div>
      )}
    </>
  );
}

function QuestionItem({ question, onReply, onEdit, isOwner }) {
  // Format the question from the API (camelCase fields from serializer)
  const asker = question.asker;
  const askerFirstName = asker?.firstName || asker?.first_name || '';
  const askerLastName = asker?.lastName || asker?.last_name || '';
  const askerName = `${askerFirstName} ${askerLastName}`.trim() || 'Unknown';
  const askerInitial = askerFirstName ? askerFirstName.charAt(0).toUpperCase() : '?';
  const askerColor = asker?.avatarColor || '#6B7F4A';
  const questionText = question.question_text || question.content;
  const isAnswered = question.status === 'answered' || question.hasExpansion;

  // Check if this is a voice/video message
  const hasMedia = question.mediaUrl || question.media_file;
  const mediaUrl = question.mediaUrl;
  const mediaType = question.mediaType || 'audio';
  const mediaDuration = question.mediaDuration;

  // Check if it's just a placeholder text like "[Voice message]"
  const isMediaOnlyQuestion = questionText === '[Voice message]' || questionText === '[Video message]';

  return (
    <div className={`comment ${isAnswered ? '' : 'comment-question-highlight'}`}>
      <div className="comment-avatar" style={{ backgroundColor: askerColor, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', width: 32, height: 32, fontSize: 14, fontWeight: 'bold' }}>
        <span>{askerInitial}</span>
      </div>
      <div className="comment-content">
        <div className="comment-header">
          <span className="commenter-name"><MemberNameLink memberId={asker?.id} name={askerName} /></span>
          {isOwner && (
            <button className="edit-message-btn" onClick={() => onEdit?.(question)} title="Edit">
              <Pencil size={12} />
            </button>
          )}
        </div>
        {hasMedia ? (
          <>
            <VoiceNote
              mediaUrl={mediaUrl}
              mediaType={mediaType}
              duration={mediaDuration}
            />
            {!isMediaOnlyQuestion && questionText && (
              <div className={isAnswered ? 'comment-bubble' : 'comment-bubble question-bubble'}>
                <p>{questionText}</p>
              </div>
            )}
          </>
        ) : (
          <div className={isAnswered ? 'comment-bubble' : 'comment-bubble question-bubble'}>
            <p>{questionText}</p>
          </div>
        )}
        {!isAnswered && (
          <button className="reply-btn" onClick={() => onReply?.(question.id)}>
            Reply
          </button>
        )}
      </div>
    </div>
  );
}

function ExpansionItem({ expansion }) {
  // Format the expansion (answer) from the API (uses 'author' not 'responder')
  const author = expansion.author;
  const authorFirstName = author?.firstName || author?.first_name || '';
  const authorLastName = author?.lastName || author?.last_name || '';
  const authorName = `${authorFirstName} ${authorLastName}`.trim() || 'Unknown';
  const authorInitial = authorFirstName ? authorFirstName.charAt(0).toUpperCase() : '?';
  const authorColor = author?.avatarColor || '#556B2F';
  const expansionText = expansion.text_content || expansion.expansion_text || expansion.content;
  const hasAudio = expansion.audioUrl || expansion.audio_file;
  const audioUrl = expansion.audioUrl;

  return (
    <div className="comment">
      <div className="comment-avatar" style={{ backgroundColor: authorColor, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', width: 32, height: 32, fontSize: 14, fontWeight: 'bold' }}>
        <span>{authorInitial}</span>
      </div>
      <div className="comment-content">
        <span className="commenter-name"><MemberNameLink memberId={author?.id} name={authorName} /></span>
        {hasAudio ? (
          <VoiceNote
            mediaUrl={audioUrl}
            mediaType="audio"
            duration={expansion.audio_duration}
          />
        ) : (
          <div className="comment-bubble expansion-bubble">
            <p>{expansionText}</p>
          </div>
        )}
        {expansion.reactionCounts && Object.keys(expansion.reactionCounts).length > 0 && (
          <div className="expansion-reactions">
            {Object.entries(expansion.reactionCounts).map(([type, count]) => (
              count > 0 && <span key={type} className="reaction-badge">{getReactionEmoji(type)} {count}</span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function getReactionEmoji(type) {
  const emojis = {
    heart: '❤️',
    thank_you: '🙏',
    learned: '💡',
    treasure: '💎'
  };
  return emojis[type] || '👍';
}

function FamilyConversation({
  questions = [],
  expansions = [],
  loading = false,
  onReply,
  onComment,
  onUpdateQuestion,
  variant = 'default'
}) {
  const [inputValue, setInputValue] = useState('');
  const [replyingTo, setReplyingTo] = useState(null);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [showMediaCapture, setShowMediaCapture] = useState(false);
  const [captureMode, setCaptureMode] = useState('audio');
  const [capturedMedia, setCapturedMedia] = useState(null);
  const [isPlayingPreview, setIsPlayingPreview] = useState(false);
  const inputRef = React.useRef(null);

  // Get current user's person_id
  const getCurrentUserPersonId = () => {
    try {
      const user = getUser();
      if (user) {
        return user.person_id;
      }
    } catch (e) {
      console.error('Error getting user person_id:', e);
    }
    return null;
  };

  const currentUserPersonId = getCurrentUserPersonId();

  // Handle reply button click
  const handleReplyClick = (questionId) => {
    const question = questions.find(q => q.id === questionId);
    if (question) {
      setReplyingTo(question);
      // Focus the input field
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
    // Also call parent's onReply if provided
    onReply?.(questionId);
  };

  // Cancel reply
  const cancelReply = () => {
    setReplyingTo(null);
  };

  // Handle edit button click
  const handleEditClick = (question) => {
    setEditingQuestion(question);
    setInputValue(question.question_text || question.content || '');
    setReplyingTo(null); // Clear reply mode if active
    setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
  };

  // Cancel edit
  const cancelEdit = () => {
    setEditingQuestion(null);
    setInputValue('');
  };

  // Media capture handlers
  const handleMicClick = () => {
    setCaptureMode('audio');
    setShowMediaCapture(true);
  };

  const handleCameraClick = () => {
    setCaptureMode('video');
    setShowMediaCapture(true);
  };

  const handleMediaCaptured = (mediaData) => {
    setCapturedMedia(mediaData);
    setShowMediaCapture(false);
  };

  const handleRemoveMedia = () => {
    if (capturedMedia?.preview) {
      URL.revokeObjectURL(capturedMedia.preview);
    }
    setCapturedMedia(null);
    setIsPlayingPreview(false);
  };

  const formatMediaDuration = (seconds) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const togglePreviewPlayback = (e) => {
    e.stopPropagation();
    const mediaElement = document.getElementById('conversation-media-preview');
    if (mediaElement) {
      if (isPlayingPreview) {
        mediaElement.pause();
      } else {
        mediaElement.play();
      }
      setIsPlayingPreview(!isPlayingPreview);
    }
  };

  // Combine questions and their expansions into a conversation thread
  const conversationItems = [];

  // Add questions and their associated expansions
  questions.forEach(question => {
    conversationItems.push({
      type: 'question',
      data: question,
      timestamp: new Date(question.created_at || question.asked_at)
    });

    // Find expansions that answer this question
    const relatedExpansions = expansions.filter(exp => exp.question === question.id);
    relatedExpansions.forEach(expansion => {
      conversationItems.push({
        type: 'expansion',
        data: expansion,
        timestamp: new Date(expansion.created_at)
      });
    });
  });

  // Add any standalone expansions (if any)
  expansions.forEach(expansion => {
    if (!expansion.question) {
      conversationItems.push({
        type: 'expansion',
        data: expansion,
        timestamp: new Date(expansion.created_at)
      });
    }
  });

  // Sort by timestamp
  conversationItems.sort((a, b) => a.timestamp - b.timestamp);

  const handleSubmit = (e) => {
    e.preventDefault();
    const hasContent = inputValue.trim() || capturedMedia;

    if (hasContent) {
      // If editing a question, call update (text only)
      if (editingQuestion) {
        onUpdateQuestion?.(editingQuestion.id, inputValue.trim());
        setEditingQuestion(null);
        setInputValue('');
        return;
      }

      // Build comment data with optional media
      const commentData = {
        text: inputValue.trim(),
        media: capturedMedia
      };

      // If replying to a question, include the question context
      if (replyingTo) {
        onComment?.(commentData, replyingTo.id);
        setReplyingTo(null);
      } else {
        onComment?.(commentData);
      }
      setInputValue('');
      setCapturedMedia(null);
    }
  };

  const hasContent = inputValue.trim() || capturedMedia;

  const hasConversation = conversationItems.length > 0;

  return (
    <div className="sidebar-card family-conversation">
      <h3 className="sidebar-title">
        <span>💬</span> Family Conversation
      </h3>

      <div className="comments-list">
        {loading && (
          <div className="conversation-loading">
            <div className="loading-spinner-small"></div>
            <span>Loading conversation...</span>
          </div>
        )}

        {!loading && !hasConversation && (
          <div className="no-conversation">
            <MessageCircle size={24} className="no-conversation-icon" />
            <p>No conversation yet</p>
            <span>Be the first to ask a question or share a thought!</span>
          </div>
        )}

        {!loading && hasConversation && conversationItems.map((item, index) => (
          item.type === 'question' ? (
            <QuestionItem
              key={`q-${item.data.id || index}`}
              question={item.data}
              onReply={handleReplyClick}
              onEdit={handleEditClick}
              isOwner={currentUserPersonId && item.data.asker?.id === currentUserPersonId}
            />
          ) : (
            <ExpansionItem
              key={`e-${item.data.id || index}`}
              expansion={item.data}
            />
          )
        ))}

        {variant === 'media-only' && !hasConversation && !loading && (
          <div className="curious-prompt">
            <span className="curious-icon">💭</span>
            <div className="curious-content">
              <strong>Family is curious!</strong>
              <p>Add a story to help them understand these moments</p>
            </div>
          </div>
        )}
      </div>

      {editingQuestion && (
        <div className="editing-banner">
          <span className="editing-text">
            Editing your message
          </span>
          <button type="button" className="cancel-edit-btn" onClick={cancelEdit}>
            <X size={14} />
          </button>
        </div>
      )}
      {replyingTo && !editingQuestion && (
        <div className="replying-to-banner">
          <span className="replying-to-text">
            Replying to: "{(replyingTo.question_text || replyingTo.content || '').substring(0, 50)}..."
          </span>
          <button type="button" className="cancel-reply-btn" onClick={cancelReply}>
            &times;
          </button>
        </div>
      )}
      <div className="conversation-input-container">
        {/* Media Preview */}
        {capturedMedia && (
          <div className="conversation-media-preview">
            <div className="conversation-media-preview-content">
              {capturedMedia.type === 'audio' ? (
                <div className="conv-audio-preview">
                  <button
                    type="button"
                    className="conv-preview-play-btn"
                    onClick={togglePreviewPlayback}
                  >
                    {isPlayingPreview ? '❚❚' : <Play size={14} fill="#fff" />}
                  </button>
                  <div className="conv-preview-info">
                    <span className="conv-preview-type">Voice message</span>
                    <span className="conv-preview-duration">{formatMediaDuration(capturedMedia.duration)}</span>
                  </div>
                  <audio
                    id="conversation-media-preview"
                    src={capturedMedia.preview}
                    onEnded={() => setIsPlayingPreview(false)}
                    style={{ display: 'none' }}
                  />
                </div>
              ) : (
                <div className="conv-video-preview">
                  <video
                    id="conversation-media-preview"
                    src={capturedMedia.preview}
                    onEnded={() => setIsPlayingPreview(false)}
                    style={{ width: '50px', height: '38px', objectFit: 'cover', borderRadius: '4px' }}
                  />
                  <div className="conv-preview-info">
                    <span className="conv-preview-type">Video message</span>
                    <span className="conv-preview-duration">{formatMediaDuration(capturedMedia.duration)}</span>
                  </div>
                </div>
              )}
              <button
                type="button"
                className="conv-preview-remove-btn"
                onClick={handleRemoveMedia}
                aria-label="Remove media"
              >
                <X size={12} />
              </button>
            </div>
          </div>
        )}

        <form className="conversation-input" onSubmit={handleSubmit}>
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder={
              editingQuestion
                ? "Edit your message..."
                : replyingTo
                ? "Write your reply..."
                : capturedMedia
                ? "Add a caption (optional)..."
                : "Add a memory or ask a question..."
            }
            className="conversation-input-field"
          />
          <div className="conversation-input-actions">
            <button
              type="button"
              className={`conv-action-btn ${capturedMedia?.type === 'audio' ? 'active' : ''}`}
              onClick={handleMicClick}
              title="Record voice message"
            >
              <Mic size={16} />
            </button>
            <button
              type="button"
              className={`conv-action-btn ${capturedMedia?.type === 'video' ? 'active' : ''}`}
              onClick={handleCameraClick}
              title="Record video message"
            >
              <Camera size={16} />
            </button>
            {hasContent && (
              <button type="submit" className="conv-send-btn">
                <Send size={16} />
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Media Capture Modal */}
      <MediaCapture
        isOpen={showMediaCapture}
        onClose={() => setShowMediaCapture(false)}
        onMediaCaptured={handleMediaCaptured}
        initialMode={captureMode}
      />
    </div>
  );
}

export default FamilyConversation;
